// ============================================================
// painelGeral.js — uma linha por atleta, com tudo que foi medido
//
// A ficha do atleta responde "como ESTE atleta está", e responde bem. O
// que não existia era a pergunta de cima: como está o programa. Para
// responder, alguém abria a ficha de trinta e cinco atletas, uma por uma,
// e anotava num papel à parte.
//
// ── O que este módulo NÃO faz ───────────────────────────────
//
// Nenhuma conta. Nem uma. Tudo aqui sai das mesmas funções que alimentam
// a ficha, o Excel e o PDF:
//
//   composição corporal    compilarSerie         (avaliacaoFisica.js)
//   avaliação técnica      compilarSerieTecnica  (avaliacaoTecnica.js)
//   testes físicos         compilarSerieDeTestes (testesFisicos.js)
//   escore global          serieDeEscores        (desempenho.js)
//   ranking                historicoDoAtleta     (rankings.js)
//
// É de propósito, e é a regra mais importante do arquivo: número que o
// painel calculasse por conta própria discordaria da ficha do mesmo
// atleta algum dia, e aí não haveria como saber qual dos dois está certo.
// Este módulo só escolhe o ÚLTIMO ponto de cada série e põe lado a lado.
//
// ── Vazio é resposta ────────────────────────────────────────
//
// Atleta sem avaliação física não tem "0% de gordura", tem null. Zero numa
// coluna de medida é uma medida, e quem lê a tabela decide coisas a partir
// dela. Por isso toda ausência sai como null e a tela escreve "não
// medido", que é outra coisa.
//
// E é a ausência que torna o painel útil: a leitura que a coordenação não
// tinha é quem FALTA medir, não quem já foi medido.
// ============================================================

import { compilarSerie } from "./avaliacaoFisica";
import { compilarSerieTecnica, modeloDe } from "./avaliacaoTecnica";
import { compilarSerieDeTestes, bateriaDa } from "./testesFisicos";
import { serieDeEscores } from "./desempenho";
import { historicoDoAtleta, rankingMaisRecente } from "./rankings";
import { idadeEm, idadeDecimalEm } from "./idade";

const DIACRITICOS = /[̀-ͯ]/g;

const norm = (s) => String(s || "").toLowerCase().trim()
  .normalize("NFD").replace(DIACRITICOS, "").replace(/\s+/g, " ");

export function ehAtleta(pessoa) {
  const p = pessoa || {};
  if (p.grupo) return String(p.grupo).toUpperCase() === "ATLETA";
  return norm(p.perfil).includes("atleta");
}

// O último ponto de uma série, ou null quando ela está vazia.
//
// Último, e não "melhor": o painel diz onde o atleta está hoje, e o melhor
// resultado de abril não diz isso em outubro.
function ultimo(serie) {
  const lista = Array.isArray(serie) ? serie : [];
  return lista.length ? lista[lista.length - 1] : null;
}

// Agrupa os documentos de uma coleção pelo atleta.
//
// Casa por `atletaUid` e, só quando ele falta, pelo nome. O uid é a chave
// estável; o nome é recuo para documento antigo, e é o mesmo recuo que a
// ficha usa. Casar primeiro por nome traria de volta o defeito que já
// custou abono em dispensa: nome é chave frágil.
function porAtleta(documentos, atletas) {
  const uidsConhecidos = new Set();
  const porNome = new Map();
  atletas.forEach((a) => {
    if (a.uid) uidsConhecidos.add(a.uid);
    porNome.set(norm(a.nome), a.uid);
  });

  const mapa = new Map();
  (documentos || []).forEach((d) => {
    const uid = (d.atletaUid && uidsConhecidos.has(d.atletaUid))
      ? d.atletaUid
      : porNome.get(norm(d.atletaNome));
    if (!uid) return;
    if (!mapa.has(uid)) mapa.set(uid, []);
    mapa.get(uid).push(d);
  });
  return mapa;
}

// Uma linha por atleta, com o último de cada medida.
export function linhasDoPainel({
  atletas = [],
  fisicas = [],
  tecnicas = [],
  testes = [],
  hojeISO = null,
} = {}) {
  const soAtletas = atletas.filter(ehAtleta);
  const fisPorAtleta = porAtleta(fisicas, soAtletas);
  const tecPorAtleta = porAtleta(tecnicas, soAtletas);
  const tstPorAtleta = porAtleta(testes, soAtletas);

  return soAtletas.map((a) => {
    const avaliacoesFisicas = fisPorAtleta.get(a.uid) || [];
    const avaliacoesTecnicas = tecPorAtleta.get(a.uid) || [];
    const coletas = tstPorAtleta.get(a.uid) || [];

    const serieFisica = compilarSerie({
      sexo: a.sexo || a.genero,
      dataNascimento: a.dataNascimento,
      avaliacoes: avaliacoesFisicas,
    });
    const serieTecnica = compilarSerieTecnica({
      modalidade: a.modalidade,
      avaliacoes: avaliacoesTecnicas,
    });
    const serieDeTestes = compilarSerieDeTestes({
      modalidade: a.modalidade,
      idadeEm: (data) => idadeDecimalEm(a.dataNascimento, data),
      coletas,
    });

    // O escore global casa a técnica com os TESTES, e não com a
    // antropometria: é a técnica valendo 75% e a bateria de testes 25%.
    // Quem lê "física" no escore às vezes espera a composição corporal, e
    // não é ela.
    const escores = serieDeEscores({
      tecnica: serieTecnica.map((p) => ({
        data: p.data, somatorio: p.somatorio, divisor: p.divisor, media: p.media,
      })),
      fisica: serieDeTestes.map((p) => ({
        data: p.data, somatorio: p.somatorio, divisor: p.divisor, media: p.media,
      })),
    });

    const ultFisica = ultimo(serieFisica);
    const ultTecnica = ultimo(serieTecnica);
    const ultTeste = ultimo(serieDeTestes);
    const ultEscore = ultimo(escores.filter((p) => Number.isFinite(p.global)));
    const ranking = rankingMaisRecente(historicoDoAtleta(a));

    return {
      uid: a.uid,
      nome: a.nome,
      modalidade: a.modalidade || null,
      categoria: a.categoria || null,
      sexo: a.sexo || a.genero || null,
      ativo: a.ativo !== false,
      idade: a.dataNascimento ? idadeEm(a.dataNascimento, hojeISO) : null,

      // O IMC e a gordura moram dentro de `indices` e do bloco do
      // protocolo, e não soltos no ponto da série. Lidos do lugar errado
      // vinham null para todo mundo, o que numa tabela de medidas passa
      // por "ninguém foi medido" em vez de por engano de leitura.
      //
      // O bloco se chama `deurenberg` por história: ele carrega o
      // protocolo que foi aplicado de fato, que o campo `protocolo` diz.
      fisica: ultFisica ? {
        data: ultFisica.data,
        quantas: serieFisica.length,
        peso: valorOuNulo(ultFisica.peso),
        estatura: valorOuNulo(ultFisica.estatura),
        imc: valorOuNulo((ultFisica.indices || {}).imc),
        gordura: valorOuNulo((ultFisica.deurenberg || {}).percentualGordura),
        protocoloDaGordura: (ultFisica.deurenberg || {}).protocolo || null,
        somatotipo: (ultFisica.somatotipo || {}).classificacao || null,
        maturacao: (ultFisica.maturacao || {}).estagio || null,
        // Quanto cresceu desde a primeira medida. É a leitura que explica
        // o resto: em atleta de treze anos, peso e dobras mudam porque o
        // corpo está mudando, e não porque o treino mudou.
        cresceuCm: valorOuNulo((ultFisica.desdeAPrimeira || {}).estatura),
      } : null,

      tecnica: ultTecnica ? {
        data: ultTecnica.data,
        quantas: serieTecnica.length,
        somatorio: valorOuNulo(ultTecnica.somatorio),
        divisor: valorOuNulo(ultTecnica.divisor),
        media: valorOuNulo(ultTecnica.media),
        completa: Boolean(ultTecnica.completa),
        faltam: ultTecnica.faltam || [],
        avaliador: ultTecnica.avaliador || null,
      } : null,

      testes: ultTeste ? {
        data: ultTeste.data,
        quantas: serieDeTestes.length,
        media: valorOuNulo(ultTeste.media),
        somatorio: valorOuNulo(ultTeste.somatorio),
        divisor: valorOuNulo(ultTeste.divisor),
      } : null,

      escore: ultEscore ? {
        data: ultEscore.data,
        global: ultEscore.global,
        desdeOInicio: ultEscore.desdeOInicio || null,
        dataDoInicio: ultEscore.dataDoInicio || null,
      } : null,

      ranking: ranking ? {
        ano: ranking.ano,
        estadual: ranking.estadual || "",
        nacional: ranking.nacional || "",
      } : null,
    };
  });
}

// Mantém null como null. `?? null` deixaria passar NaN, que a tela
// imprimiria como "NaN" numa coluna de medida.
function valorOuNulo(v) {
  return Number.isFinite(v) ? v : (typeof v === "string" && v ? v : null);
}

// ── Onde este painel sabe ler ────────────────────────────────
//
// A avaliação técnica e os testes físicos não são genéricos: cada
// modalidade tem a SUA bateria de testes e os SEUS critérios, com as
// tabelas de nota por idade. Hoje existem para BMX, skate e tênis de
// mesa, que são as do Ceará.
//
// Num núcleo de canoagem, remo e polo aquático, as mesmas telas abrem e
// não medem nada: a bateria vem vazia, o modelo de critérios vem vazio, e
// o painel sairia uma página de traços. Página de traços não se lê como
// "ainda não temos a fórmula desta modalidade", se lê como programa
// parado ou sistema quebrado.
//
// Por isso o painel pergunta se a modalidade tem fórmula, em vez de
// perguntar se o núcleo é o Ceará. É a mesma coisa hoje e deixa de ser no
// dia em que as fórmulas da canoagem entrarem: o painel daquele núcleo
// passa a funcionar sozinho, sem ninguém lembrar de tirar uma trava.
export function modalidadeTemFormulas(modalidade) {
  if (!modalidade) return false;
  return bateriaDa(modalidade).length > 0
    || modeloDe(modalidade).criterios.length > 0;
}

// As modalidades do conjunto, separadas pelo que o painel sabe medir.
export function formulasDoNucleo(linhas = []) {
  const modalidades = [...new Set(linhas.map((l) => l.modalidade).filter(Boolean))].sort();
  return {
    comFormula: modalidades.filter(modalidadeTemFormulas),
    semFormula: modalidades.filter((m) => !modalidadeTemFormulas(m)),
  };
}

// ── Quem falta medir ─────────────────────────────────────────
//
// A contagem considera só o atleta ATIVO. Quem foi desligado não falta ser
// avaliado, e contá-lo faria a cobertura parecer pior do que é, o que
// mandaria a coordenação atrás de gente que não treina mais.
export const MEDIDAS = [
  { chave: "fisica", rotulo: "Avaliação física" },
  { chave: "tecnica", rotulo: "Avaliação técnica" },
  { chave: "testes", rotulo: "Testes físicos" },
  { chave: "escore", rotulo: "Escore global" },
];

export function coberturaDoPainel(linhas = []) {
  const ativos = linhas.filter((l) => l.ativo);
  return MEDIDAS.map(({ chave, rotulo }) => {
    const sem = ativos.filter((l) => !l[chave]);
    return {
      chave,
      rotulo,
      com: ativos.length - sem.length,
      total: ativos.length,
      semMedida: sem.map((l) => l.nome),
    };
  });
}

// A data mais recente de cada medida, no conjunto todo.
//
// É o que diz se o programa está medindo ou parou de medir: cobertura
// cheia com a última coleta de abril não é um programa em dia. O escore
// fica fora porque ele não é uma coleta, é a combinação de duas.
export function ultimasDatas(linhas = []) {
  return MEDIDAS.filter((m) => m.chave !== "escore").map(({ chave, rotulo }) => {
    const datas = linhas.map((l) => l[chave] && l[chave].data).filter(Boolean).sort();
    return { chave, rotulo, data: datas.length ? datas[datas.length - 1] : null };
  });
}
